import React from 'react';
import PropTypes from 'prop-types';
import { designSystem, reactHookForm } from '@yola/ws-ui';
import constants from '../constants';
import { createKeywordOption } from '../helpers/get-keywords-list';
import SearchPreview from './search-preview';

const {
  Stack,
  Heading,
  ControlGroup,
  CheckboxGroup,
  Mark,
  InputGroupField,
  CheckboxField,
  TextAreaField,
  TagInputField,
  FieldStatus,
} = designSystem;
const { useWatch } = reactHookForm;

const { fieldNames } = constants;

function SeoSection({
  control,
  fieldRefs,
  captions,
  pathPrefix,
  businessName,
  isBusinessInfoInWebsiteSettingsFeatureFlagEnabled,
  onChangeSameAsPageName,
}) {
  const [pageTitleValue, pagePathValue, pageDescriptionValue, sameAsPageName, displayBusinessName] =
    useWatch({
      control,
      name: [
        fieldNames.TITLE,
        fieldNames.PATH,
        fieldNames.DESCRIPTION,
        fieldNames.SAME_AS_PAGE_NAME,
        fieldNames.DISPLAY_BUSINESS_NAME,
      ],
    });

  let title = pageTitleValue;
  const path = `${pathPrefix}${pagePathValue}`;
  const description = pageDescriptionValue?.trim()
    ? pageDescriptionValue
    : captions.searchPreview.description;

  if (isBusinessInfoInWebsiteSettingsFeatureFlagEnabled && businessName && displayBusinessName) {
    if (title) {
      title = `${title} | ${businessName}`;
    } else {
      title = businessName;
    }
  }

  return (
    <Stack gap="spacing-m">
      <Stack gap="spacing-3-xs">
        <Heading type="heading-6">{captions.searchPreview.heading}</Heading>
        <SearchPreview
          title={title || captions.searchPreview.title}
          path={path}
          description={description}
        />
      </Stack>

      <ControlGroup title={captions.title.name}>
        <div ref={fieldRefs[fieldNames.TITLE]} className="ws-page-settings__field-wrapper">
          <InputGroupField name={fieldNames.TITLE} control={control} disabled={sameAsPageName} />
          <FieldStatus text={captions.title.description} />
        </div>
      </ControlGroup>

      <CheckboxGroup>
        <CheckboxField
          name={fieldNames.SAME_AS_PAGE_NAME}
          control={control}
          label={captions.sameAsPageName.label}
          onChange={onChangeSameAsPageName}
        />

        {isBusinessInfoInWebsiteSettingsFeatureFlagEnabled && (
          <CheckboxField
            name={fieldNames.DISPLAY_BUSINESS_NAME}
            control={control}
            label={captions.displayBusinessName.label}
            description={
              !businessName && (
                <>
                  {captions.displayBusinessName.description}
                  <Mark appearance="high-emphasis">
                    {captions.displayBusinessName.settingsTitle} &#x2192;{' '}
                    {captions.displayBusinessName.businessInfoTitle}
                  </Mark>
                </>
              )
            }
            disabled={!businessName}
          />
        )}
      </CheckboxGroup>

      <CheckboxField
        name={fieldNames.PREVENT_INDEXING}
        control={control}
        label={captions.preventIndexing.name}
        description={captions.preventIndexing.description}
      />

      <ControlGroup title={captions.description.name}>
        <div ref={fieldRefs[fieldNames.DESCRIPTION]} className="ws-page-settings__field-wrapper">
          <TextAreaField name={fieldNames.DESCRIPTION} control={control} resize />
          <FieldStatus text={captions.description.description} />
        </div>
      </ControlGroup>

      <ControlGroup title={captions.keywords.name}>
        <TagInputField
          name={fieldNames.KEYWORDS}
          control={control}
          isTagTextClipped={false}
          shouldCreateTagOnPaste
          onCreateOption={createKeywordOption}
        />
        <FieldStatus text={captions.keywords.description} />
      </ControlGroup>
    </Stack>
  );
}

SeoSection.propTypes = {
  control: PropTypes.shape().isRequired,
  fieldRefs: PropTypes.shape().isRequired,
  captions: PropTypes.shape().isRequired,
  pathPrefix: PropTypes.string.isRequired,
  businessName: PropTypes.string.isRequired,
  isBusinessInfoInWebsiteSettingsFeatureFlagEnabled: PropTypes.bool.isRequired,
  onChangeSameAsPageName: PropTypes.func.isRequired,
};

export default SeoSection;
